import { InvoiceStatusEnum, PlanTypeEnum } from '@/types/generated/graphql';
import { WatchQueryFetchPolicy } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import MasterCardLogo from '@/assets/static/images/mastercard.png';
import ApplePayLogo from '@/assets/static/images/applepay.png';
import GPayLogo from '@/assets/static/images/gpay.png';
import VisaCardLogo from '@/assets/static/images/visacard.png';
import PayPalLogo from '@/assets/static/images/paypal.png';
import { ImageConfig, ImageType } from '@modules/my-account/my-account.types';
import { config } from '@/lib/env-helpers';
import { CALILIO_NODE_ENV } from './env';
import { capitalize } from '@shared/utils/capitalize';

dayjs.extend(utc);

export const appContentSpace = 'px-4 md:px-13';
export const date_format = 'YYYY-MM-DD';
export const duration_format = 'H[h] m[m] ss[s]';
export const sidebar_width = 260;
export const sub_sidebar_width = 290;
export const drawer_width = 320;
export const PAGE_SIZE_SMALL_CLASS = 'max-w-[770px]';
export const PAGE_SIZE_MEDIUM_CLASS = 'max-w-[1082px]';

export const RESEND_CONFIRMATION_CODE_REQUEST_DELAY = 120; // seconds;
export const CONFIRMATION_CODE_EXPIRY = 300; // seconds
export const RESET_CODE_EXPIRY = 300; // seconds
export const RESEND_RESET_DELAY = 120; // seconds;
export const DEFAULT_API_TIMEOUT = 7000; // milliseconds
export const MAX_MESSAGE_LENGTH = 1600;

export const SENTIMENT_TYPE = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  NEUTRAL: 'NEUTRAL',
  NOT_APPLICABLE: 'NOT-APPLICABLE',
};

export const INTENT_TYPE = {
  REASON: 'reason',
  CONCLUSION: 'conclusion',
  CONCLUSION_RESOLUTION: 'conclusion_resolution',
};

export const RESOLUTION_OUTCOME_CATEGORY = {
  CLOSED: 'closed',
  OPEN: 'open',
  NOT_APPLICABLE: 'not_applicable',
};

export const CALL_DIRECTION = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
} as const;

export const OVERVIEW_TABS = {
  OVERVIEW: 'Overview',
  USAGE_HISTORY: 'Usage History',
} as const;

export const defaultAvatar = 'https://api.dicebear.com/7.x/miniavs/svg?seed=1';

export const symbols_hex = {
  triangle_up: '\u2BC5',
  triangle_down: '\u2BC6',
  long_dash: '\u2015',
  white_dot: '\u25CF',
  cross: '\u2715',
};

export const trend = {
  last24H: 'last_24_hours',
  last7D: 'last_week',
  last30D: 'last_month',
  last3M: 'last_3_months',
  last12M: 'last_year',
  last24M: 'last_2_years',
};

export const PASSWORD_REGEX = {
  SYMBOL_NUM: /^(?=.*[0-9])(?=.*[!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~])[a-zA-Z\d!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]+$/,
  CASE: /^(?=.*[a-z])(?=.*[A-Z]).+$/,
};

export const CONVERSATION_DIRECTION = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
} as const;

export const CALL_STATUS = {
  ORIGINATED: 'originated',
  INITIATED: 'initiated',
  QUEUED: 'queued',
  RINGING: 'ringing',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  BUSY: 'busy',
  FAILED: 'failed',
  NO_ANSWER: 'no-answer',
  CANCELED: 'canceled',
} as const;

export const MESSAGE_STATUS = {
  QUEUED: 'queued',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  RECEIVED: 'received',
  SENT: 'sent',
  UNDELIVERED: 'undelivered',
} as const;

export const CONVERSATION_TYPE = {
  CONFERENCE: 'conference',
  MESSAGE: 'message',
} as const;
export const currentUTCTimestamp = dayjs.utc().format(date_format);

export const LocalStorageKeys = {
  device_id: 'deviceId',
  tokens: '_tokens',
  workspaceSecret: '_wks',
  claimNumberSkipped: 'claimNumberSkipped',
  contactSourceTypeFilter: 'contactSourceTypeFilter',
  lastViewedConversationClient: 'lastViewedConversationClient',
};

export const PlanType = {
  BASIC: 'basic',
  BUSINESS: 'business',
  CUSTOM: 'custom',
} as const;

export const PLAN_TYPE_LABELS: Record<string, string> = {
  [PlanType.BASIC]: 'Standard Plan',
  [PlanType.BUSINESS]: 'Premium Plan',
  [PlanType.CUSTOM]: 'Enterprise Plan',
};

export const TWILIO_DEVICE_EVENTS = {
  registered: 'registered',
  incoming: 'incoming',
  tokenWillExpire: 'tokenWillExpire',
  unregistered: 'unregistered',
  destroyed: 'destroyed',
  error: 'error',
};

export const TWILIO_CALL_EVENTS = {
  accept: 'accept',
  cancel: 'cancel',
  disconnect: 'disconnect',
  ringing: 'ringing',
  reject: 'reject',
};

export const COOKIE_KEY = {
  deviceId: config.APP_ENV + '-x-device-id',
  accessToken: config.APP_ENV + '-access_token',
  workspaceSecret: config.APP_ENV + '-x-workspace-secret-id',
  sessionId: config.APP_ENV + '-sessionId',
  refreshToken: config.APP_ENV + '-refreshToken',
  locale: 'locale',
};

export const HTTP_HEADERS = {
  DEVICE_ID: 'x-device-id',
  AUTHORIZATION: 'Authorization',
  WORKSPACE_SECRET: 'x-workspace-secret-id',
  SESSION_ID: 'sessionId',
  REFRESH_TOKEN: 'refreshToken',
  LOCALE: 'accept-language',
  IDEMPOTENCY_KEY: 'Idempotency-Key',
};

export const SLUG_TO_PLAN = {
  basic: PlanTypeEnum.basic,
  premium: PlanTypeEnum.business,
  enterprise: PlanTypeEnum.custom,
};

export const CALL_CUSTOM_PARAMETERS = {
  FROM: 'From',
  TO: 'To',
  CLIENT_ID: 'clientId',
  CLIENT_NAME: 'clientName',
  CLIENT_PHONE_NUMBER: 'clientPhoneNumber',
  CHANNEL_ID: 'channelId',
};

export const tagPalette = [
  '#EF4444',
  '#F54900',
  '#E17100',
  '#F0B100',
  '#00C950',
  '#EDEEEF',
  '#00BBA7',
  '#00B8DB',
  '#2B7FFF',
  '#615FFF',
  '#AD46FF',
  '#F6339A',
  '#00BC7D',
  '#D9D9DE',
];

export const TAG_COLOR = {
  RED: '#EF4444',
  ORANGE: '#F97316',
  AMBER: '#F59E0B',
  YELLOW: '#EAB308',
  GREEN: '#22C55E',
  EMERALD: '#10B981',
  TEAL: '#14B8A6',
  CYAN: '#06B6D4',
  BLUE: '#3B82F6',
  INDIGO: '#6366F1',
  VIOLET: '#8B5CF6',
  PINK: '#EC4899',
  PRIMARY: '#1E3A8A',
  GRAY: '#D1D5DB',
};

export enum TagOperationMode {
  ASSIGN_TAGS_TO_CONTACT = 'assign',
  ADD_OR_EDIT_TAG_DEFINITION = 'create-or-edit',
}
export type TagOperationModeType =
  | TagOperationMode.ASSIGN_TAGS_TO_CONTACT
  | TagOperationMode.ADD_OR_EDIT_TAG_DEFINITION;

export const cacheOnly: { fetchPolicy: WatchQueryFetchPolicy; nextFetchPolicy: WatchQueryFetchPolicy } = {
  fetchPolicy: 'cache-only',
  nextFetchPolicy: 'cache-only',
};

export const WALLET_AMOUNT_THRESHOLDS = {
  LOW: 5,
  MEDIUM: 10,
} as const;

export const USAGE_HISTORY_FILTER_SUB_CONTENT = {
  ACTIVITY: 'activity',
  DATE: 'date',
} as const;

export const planFeatureTooltips = {
  'Free 1 Local Number': 'US or Canada',
  'Call Recordings and Storage': 'Optional',
  'Advance Reporting': 'Coming Soon',
  'Buy or Port Virtual Numbers': '100+ countries',
  'Inbound Call Management': 'IVR, Business Hours',
  'Outbound Call Management': 'Power dialer, Call disposition',
  'Live Call Monitoring': 'Listen, Whisper, Barge-in & End-calls',
  'AI Call Reporting': ' Transcription, Sentiment analysis and more',
  'Internal Team Communication': 'Calls, Messages, Attach Files',
};

export const paymentLogos = [
  { src: VisaCardLogo, alt: 'VisaCard Logo' },
  { src: MasterCardLogo, alt: 'MasterCard Logo' },
  { src: PayPalLogo, alt: 'PayPal Logo' },
  { src: ApplePayLogo, alt: 'Apple Pay Logo' },
  { src: GPayLogo, alt: 'Google Pay Logo' },
];

export const WALLET_AMOUNT_THRESHOLD_COLORS = {
  LOW: 'text-red-700',
  MEDIUM: 'text-amber-700',
  HIGH: 'text-text-900',
} as const;

export const custom_events = {
  save_as_draft: 'save-as-draft',
  caller_id_verified: 'caller-id-verified',
  import_progress: 'import-progress',
};

export const FILE_SIZE = ['B', 'KB', 'MB', 'GB'] as const;

export const VALID_FILE_TYPES = [
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
] as const;

export const CONTACT_IMPORT_MAX_FILE_SIZE_MB = 15;

export const CSV_FILE = {
  IMPORT_FILE: 'https://calilio-assets.s3.amazonaws.com/contactsUploadTemplate.csv',
} as const;
export const MEMBER_INVITE_ROLE_CHOICE = [
  { label: 'Admin', value: 'admin' },
  { label: 'Member', value: 'member' },
];

export const SELECTED_COUNTRY_CODE = 'dialer_selected_country';

export const DEFAULT_CURRENCY = '$';

export const ASSIGN_TAG_LIMIT = 21;
export const TAG_LIMIT_ERROR_MESSAGE = `You’ve reached the ${ASSIGN_TAG_LIMIT} tag limit. Please remove tags to add more.`;

export const WALLET_MODAL_TYPE = {
  loadWallet: 'load-wallet',
  autoRecharge: 'auto-recharge',
  lowBalance: 'low-balance',
  successModal: 'success-modal',
};

export const IMAGE_CONFIG: Record<ImageType, ImageConfig> = {
  profile: {
    cacheKey: 'profilePicture',
    variableKey: 'profileId',
  },
  cover: {
    cacheKey: 'coverProfilePicture',
    variableKey: 'coverProfilePictureId',
  },
  workspaceLogo: {
    cacheKey: 'workspaceLogo',
    variableKey: 'logoId',
  },
  workspaceCoverImage: {
    cacheKey: 'workspaceCoverImage',
    variableKey: 'coverProfileId',
  },
};

export const WALLET_LIMIT = {
  LOAD_WALLET: 5,
  MAX_LOAD_WALLET: 2_000,
  LOW_BALANCE: 10,
  AUTO_RECHARGE_THRESHOLD: 5,
  AUTO_RECHARGE_THRESHOLD_MAX: 250,
  AUTO_RECHARGE_CREDIT: 15,
  AUTO_RECHARGE_CREDIT_MAX: 2_000,
  MAX_BALANCE_NOTIFICATION: 250,
};

export const DATE_OPTIONS = (t: (key: string, options?: Record<string, unknown>) => string) =>
  [
    { value: 'last-24-hours', label: t('last_x_hour', { count: 24 }) },
    { value: 'last-7-days', label: t('last_x_day', { count: 7 }) },
    { value: 'last-30-days', label: t('last_x_day', { count: 30 }) },
    { value: 'last-3-months', label: t('last_x_month', { count: 3 }) },
    { value: 'last-12-months', label: t('last_x_month', { count: 12 }) },
    { value: 'last-24-months', label: t('last_x_month', { count: 24 }) },
  ] as const;

export const WALLET_ACTIVITY_OPTIONS = (t: (key: string, options?: Record<string, unknown>) => string) =>
  [
    { value: 'load-wallet', label: capitalize(t('load_wallet')) },
    { value: 'incoming-message', label: capitalize(t('incoming_message_other')) },
    { value: 'incoming-call', label: capitalize(t('incoming_call_other')) },
    { value: 'outgoing-message', label: capitalize(t('outgoing_message_other')) },
    { value: 'outgoing-call', label: capitalize(t('outgoing_call_other')) },
  ] as const;

export const MAX_PIN_CONVERSATION_COUNT = 25;

export const TRANSACTION_STATUS_OPTIONS = (t: (key: string, options?: Record<string, unknown>) => string) =>
  [
    { label: capitalize(t('success')), value: InvoiceStatusEnum.paid },
    { label: capitalize(t('failed')), value: InvoiceStatusEnum.not_paid },
    { label: capitalize(t('pending')), value: InvoiceStatusEnum.pending },
    { label: capitalize(t('payment_due')), value: InvoiceStatusEnum.payment_due },
  ] as const;
export const TWILIO_IMAGE_FILE_EXTENSIONS = '.jpeg,.jpg,.gif,.png,.heic,.heif,.tiff,.bmp';
export const TWILIO_AUDIO_FILE_EXTENSIONS = '.ogg,.mpeg,.mp3,.3gpp,.3gpp2,.basic,.L24,.ac3,.webm,.amr';
export const TWILIO_MMS_FILE_EXTENSIONS = TWILIO_IMAGE_FILE_EXTENSIONS + ',' + TWILIO_AUDIO_FILE_EXTENSIONS;

export const media_upload_status = {
  uploading: 'uploading',
  done: 'done',
  error: 'error',
};

export const REDIRECT_INVOICE_TYPES = ['EXCLUSIVE_NUMBER', 'A2P_LOW_VOLUME', 'PORT_IN'];
export const NOTIFICATION_REDIRECTION_KEY = {
  CHAT_BOX: 'chat_box',
  PHONE_NUMBER: 'phone_number',
  MEMBER_CHAT: 'member_chat',
};

export const NOTIFICATION_TYPE = {
  VOICE_MAIL: 'voiceMail',
  MISSED_CALL: 'missedCall',
  ASSIGN_CHANNEL: 'assignChannel',
  FEATURE_UPDATE: 'featureUpdate',
  SOFTWARE_MAINTENANCE: 'softwareMaintenance',
};
export const KEYPAD_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'];

export const VOICE_THRESHOLD = 30;

export const ATTRIBUTE_NAME = {
  timezone: 'timezone',
  documentType: 'document_type',
};

export const FEATURES_HOME_URL_RUNTIME_ENV = {
  [CALILIO_NODE_ENV.LOCAL]: 'http://localhost:3001',
  [CALILIO_NODE_ENV.DEVELOP]: 'https://features.internal.krispchats.com',
  [CALILIO_NODE_ENV.STAGING]: 'https://features.trial.krispchats.com',
  [CALILIO_NODE_ENV.UAT]: 'https://features.uat.calilio.com',
  [CALILIO_NODE_ENV.PRODUCTION]: 'https://features.calilio.com',
};

export const rate_limit_percentage = {
  green: 50,
  yellow: 75,
  red: 99,
};

export const fallbackTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export const TIME_FORMATS = {
  UTC: 'YYYY-MM-DDTHH:mm:ss',
  DISPLAY_DATE: 'MMM D',
  DISPLAY_TIME: '[at] h:mm A',
  DISPLAY_TIME2: 'h:mm A',
  DISPLAY_DAY_TIME: 'ddd, hh:mm A',
  DATE_ONLY: 'YYYY-MM-DD',
  TIME_ONLY: 'HH:mm:ss',
  DATE_TIME_MS: 'YYYY-MM-DD HH:mm:ss.SSS',
} as const;

export const old_design_storage = {
  authToken: 'app_calilio_auth_token',
  authUser: 'app_calilio_auth_user',
  localStorage: {
    tokens: '_tokens',
    users: '_users',
    voiceToken: '_voice-tk',
    voiceTokenCreatedAt: '_voice-tk-created_at',
    wksSecret: '_wks',
    deviceId: 'deviceId',
    loginUser: 'login_user',
    fcmToken: 'fcmToken',
  },
};

export const CALILIO_DIALER_EXTENSION_LINK =
  'https://chromewebstore.google.com/detail/ddgfhdliefegceoofdbmlhfbddkabhdc?utm_source=item-share-cb';
